
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogTitle, MatDialogContent, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { FileUploadModule, FileUploadValidators } from '@iplab/ngx-file-upload';
import { Configuracion } from '../../models/Util';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ScnackbarData, ToastService, SnackbarStyles } from '../toast.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer } from '@angular/platform-browser';
import { ConfiguracionService } from '../../services/configuracion.service';

@Component({
    selector: 'app-dlg-editar-parametro-configuracion',
    imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    ReactiveFormsModule,
    FileUploadModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule
],
    templateUrl: './dlg-editar-parametro-configuracion.component.html',
    styleUrl: './dlg-editar-parametro-configuracion.component.scss'
})
export class DlgEditarParametroConfiguracionComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<DlgEditarParametroConfiguracionComponent>)
  readonly data = inject<Configuracion>(MAT_DIALOG_DATA);

  configuracion: Configuracion

  filesLimitMessageError = ""
  numberOfFilesLimit = 1
  filesSizeLimit = 41943040
  usedKB = 0

  patchingConfigurationParameter = false
  snackbarData: ScnackbarData = {}

  private filesControl = new FormControl<File[]>([], [
    FileUploadValidators.filesLimit(this.numberOfFilesLimit),
    FileUploadValidators.sizeLimit(this.filesSizeLimit)
  ]);

  patchConfigurationParameterForm = new FormGroup({
    files: this.filesControl,
    texto: new FormControl(""),
    numero: new FormControl(""),
    isImage: new FormControl(false),
  })

  constructor(
    private toast: ToastService,
    private _sanitizer: DomSanitizer,
    private configuracionService: ConfiguracionService
  ) {
    this.filesLimitMessageError = `M\xe1ximo ${this.numberOfFilesLimit} ${this.numberOfFilesLimit > 1 ? 'archivos' : 'archivo'} permitido${this.numberOfFilesLimit > 1 ? 's' : ''}`
    this.configuracion = structuredClone(this.data)
  }

  ngOnInit(): void {
    this.patchConfigurationParameterForm.get('files')?.valueChanges.subscribe(files => {
      if (files) {
        this.updateUsedKB(files);
      }
    });

    this.patchConfigurationParameterForm.controls.texto.setValue(this.configuracion.texto)
    this.patchConfigurationParameterForm.controls.numero.setValue(this.configuracion.numero?.toString())
    if (!this.isBase64(this.configuracion.texto)) {
      this.patchConfigurationParameterForm.controls.isImage.setValue(true)
    }
  }

  updateUsedKB(files: File[]) {
    this.usedKB = files.reduce((total, file) => total + file.size / 1024, 0); // Convertir bytes a KB
    this.updateKBsUsedDisplay();
  }

  updateKBsUsedDisplay() {
    const KB = this.filesSizeLimit / 1024
    return `${this.usedKB.toFixed(2)}/${KB.toFixed(0)} KB`;
  }

  managePatchConfigurationParameter() {
    this.patchingConfigurationParameter = true;
    const controls = this.patchConfigurationParameterForm.controls;

    let newConfiguration: Configuracion = {
      codigoConfiguracion: this.configuracion.codigoConfiguracion,
      codigo: this.configuracion.codigo,
      descripcion: this.configuracion.descripcion,
      texto: this.configuracion.texto,
      numero: parseInt(controls.numero.getRawValue()!)
    }

    if (controls.isImage && controls.files && controls.files.getRawValue()!.length > 0) {
      const file = controls.files.getRawValue()?.at(0);

      if (file) {
        this.convertFileToBase64(file).then((base64: string) => {
          newConfiguration.texto = base64
          return this.patchConfigurationParameter(newConfiguration)
        }).catch(_ => {
          this.snackbarData.message = "Error convirtiendo imagen a base64"
          this.snackbarData.style = SnackbarStyles.Error
          this.snackbarData.iconName = "error"
          this.toast.show(this.snackbarData)
          this.patchingConfigurationParameter = false
          this.dialogRef.close(null)
        });
        return
      }
    }

    newConfiguration.texto = controls.texto.getRawValue()!
    return this.patchConfigurationParameter(newConfiguration)
  }

  patchConfigurationParameter(configuracion: Configuracion) {
    this.configuracionService.patchConfigurationParameter(configuracion.codigoConfiguracion, configuracion).subscribe({
      next: (response) => {
        this.snackbarData.message = "Parámetro de configuración actualizado"
        this.snackbarData.style = SnackbarStyles.Success
        this.snackbarData.iconName = "check_circle"
        this.toast.show(this.snackbarData)
        this.patchingConfigurationParameter = false
        this.dialogRef.close(response)
      },
      error: () => {
        this.snackbarData.message = "Error actualizando parámetro de configuración"
        this.snackbarData.style = SnackbarStyles.Error
        this.snackbarData.iconName = "error"
        this.toast.show(this.snackbarData)
        this.patchingConfigurationParameter = false
        this.dialogRef.close(null)
      }
    })
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };

      reader.onerror = error => reject(error);
    });
  }

  isBase64(texto: string) {
    if (!texto) {
      return true
    }
    if (!texto.includes("base64")) {
      return true
    }
    return false
  }

  renderBase64(texto: string) {
    return this._sanitizer.bypassSecurityTrustResourceUrl("data:image/jpg;base64" + texto)
  }

}
